import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Archive } from 'lucide-react';
import { useState } from 'react';
import type { UpsertProductInput } from '@nokowazzi/types';
import { Button, Dialog, EmptyState, Spinner, toast } from '@nokowazzi/ui';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { adminProductQuery } from '../features/admin/queries';
import { ProductForm } from '../features/products/ProductForm';
import { ApiError, apiFetch } from '../lib/api';
import { jsonLocaleText } from '../lib/json-i18n';

export const Route = createFileRoute('/_admin/products/$id')({
  component: ProductEditScreen,
});

function ProductEditScreen() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productQuery = useQuery(adminProductQuery(id));
  const [archiveOpen, setArchiveOpen] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (input: UpsertProductInput) =>
      apiFetch<unknown>(`/v1/admin/products/${id}`, { method: 'PUT', json: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      toast.show({ type: 'success', message: '저장되었습니다' });
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '저장에 실패했습니다',
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => apiFetch<unknown>(`/v1/admin/products/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      toast.show({ type: 'success', message: '보관 처리되었습니다' });
      setArchiveOpen(false);
      await navigate({ to: '/products' });
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '보관 처리에 실패했습니다',
      });
    },
  });

  if (productQuery.isPending) {
    return (
      <div className="flex min-h-52 items-center justify-center">
        <Spinner label="상품 불러오는 중" />
      </div>
    );
  }

  if (productQuery.isError) {
    return (
      <EmptyState
        title="상품을 불러오지 못했습니다"
        description={
          productQuery.error instanceof ApiError
            ? productQuery.error.message
            : '잠시 후 다시 시도해 주세요'
        }
        action={
          <Button variant="tertiary" size="sm" onClick={() => void productQuery.refetch()}>
            다시 시도
          </Button>
        }
      />
    );
  }

  const product = productQuery.data;

  return (
    <>
      <PageHeader title="상품 수정" description={jsonLocaleText(product.title_i18n, 'ko')} />
      <ProductForm
        key={`${product.id}-${product.updated_at}`}
        initial={product}
        submitting={updateMutation.isPending}
        onSubmit={(input) => updateMutation.mutate(input)}
        footerExtra={
          <Can perm="products:write">
            <Button
              variant="ghost"
              size="sm"
              className="text-danger-600 hover:bg-danger-50"
              leftIcon={<Archive className="size-4" aria-hidden="true" />}
              onClick={() => setArchiveOpen(true)}
            >
              보관 처리
            </Button>
          </Can>
        }
      />
      <Dialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="상품 보관 처리"
        description="보관된 상품은 사용자에게 노출되지 않습니다. 계속하시겠습니까?"
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setArchiveOpen(false)}>
              취소
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate()}
            >
              보관 처리
            </Button>
          </>
        }
      />
    </>
  );
}
